import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
  HttpHandler
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from '../../pages/auth/services/auth.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false; // Track refresh token attempts
  private refreshTokenInProgress: Observable<any> | null = null;
  private readonly publicQrEndpoints = [
    'qr_promo_detail/',
    'qr_promo_customer/',
    'validate_qr_verification_code/'
  ];

  constructor(
      private _authService: AuthService,
      private _router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const isPublicQrRequest = this.publicQrEndpoints.some(endpoint => req.url.endsWith(endpoint));

    // QR access is a public flow. Do not attach a potentially expired admin token
    // or turn a public endpoint's 401 into an admin refresh/logout operation.
    if (isPublicQrRequest) {
      return next.handle(req.clone({
        setHeaders: { Admin: 'false' },
        url: `${environment.base_url}${req.url}`
      }));
    }

    const authTokenStr = localStorage.getItem('a_ad_token');
    const authToken = authTokenStr ? JSON.parse(authTokenStr) : null;

    req = req.clone({
      setHeaders: authToken
          ? { Authorization: `Bearer ${authToken}`, Admin: 'false' }
          : { Admin: 'false' },
      url: `${environment.base_url}${req.url}`
    });

    return next.handle(req).pipe(
     
        catchError((error: HttpErrorResponse) => {
         // console.log(req.url, 'this is the req url')
          if(error.status === 401 && req.url.endsWith('api/token/')){
          //  console.log('Here api token called')
            return throwError(error);
          }
          if (error.status === 401 && !req.url.endsWith('api/token/refresh/')) {
            return this.handle401Error(req, next);
          }
          return throwError(error);
        })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const refreshTokenStr = localStorage.getItem('r_ad_token');
    const refreshToken = refreshTokenStr ? JSON.parse(refreshTokenStr) : null;

    if (!refreshToken) {
      this.logout();
      return throwError('Session expired. Redirecting to login.');
    } 

    if (!this.refreshTokenInProgress) {
      this.isRefreshing = true;
      this.refreshTokenInProgress = this._authService.refreshToken(refreshToken).pipe(
        shareReplay(1),
        finalize(() => {
          this.isRefreshing = false;
          this.refreshTokenInProgress = null;
        })
      );
    }

    return this.refreshTokenInProgress.pipe(
      switchMap((response: any) => {
        localStorage.setItem('a_ad_token', JSON.stringify(response.access));
        localStorage.setItem('r_ad_token', JSON.stringify(response.refresh));

        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${response.access}`, Admin: 'false' },
        });

        return next.handle(authReq);
      }),
      catchError((refreshError: HttpErrorResponse) => {
        this.logout();
        return throwError(refreshError);
      })
    );
  }

  private logout() {
    // The QR code belongs to the public experience and must survive an unrelated
    // admin session expiry on the same origin.
    const cachedQrCode = localStorage.getItem('randomQrCode');
    localStorage.clear();
    if (cachedQrCode !== null) {
      localStorage.setItem('randomQrCode', cachedQrCode);
    }
    this.isRefreshing = false;
    this.refreshTokenInProgress = null;
    this._router.navigate(['/login']);
  }
}
